import type { ICellRendererParams } from 'ag-grid-community';
import { Dropdown } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PromptTopBrands } from '../components/prompts/PromptTopBrands';
import { VisibilityBar } from '../components/visibility/VisibilityBadges';
import type { TopicOverview } from '../types';
import { timeAgo } from '../utils/format';
import { getHelpDescription } from '../utils/help/help-content';

export interface TopicsColumnCallbacks {
  expandedTopicId: string | null;
  onToggleTopic: (topicId: string) => void;
  onDeleteTopic: (topic: TopicOverview) => void;
}

export function topicsColumnConfig({
  expandedTopicId,
  onToggleTopic,
  onDeleteTopic,
}: TopicsColumnCallbacks) {
  const cols: Array<Record<string, unknown>> = [
    {
      field: 'name',
      title: 'تاپیک',
      tooltip: getHelpDescription('tables.topic'),
      type: 'string',
      flex: 1,
      minWidth: 220,
      searchable: true,
      cellRenderer: 'agGroupCellRenderer',
      cellRendererParams: {
        suppressCount: true,
        innerRenderer: (params: ICellRendererParams<TopicOverview>) => {
          const row = params.data;
          if (!row) return null;
          return (
            <button
              type="button"
              className="btn btn-link p-0 text-start text-decoration-none w-100"
              onClick={e => {
                e.stopPropagation();
                onToggleTopic(row.id);
              }}
            >
              <span className="d-block fw-medium text-truncate text-body">{row.name}</span>
            </button>
          );
        },
      },
    },
    {
      field: 'promptCount',
      title: 'پرامپت‌ها',
      tooltip: getHelpDescription('tables.prompts'),
      type: 'number',
      width: 110,
      searchable: true,
      valueFormatter: (p: { value: number }) => `${p.value} پرامپت`,
    },
    {
      field: 'executionCount',
      title: 'اجراها',
      tooltip: getHelpDescription('tables.executions'),
      type: 'number',
      width: 100,
      searchable: true,
      valueFormatter: (p: { value: number }) => `${p.value} اجرا`,
    },
    {
      field: 'visibility',
      title: 'دیده‌شدن در AI',
      tooltip: getHelpDescription('metrics.ai_visibility'),
      type: 'number',
      width: 150,
      searchable: true,
      cellRenderer: (params: ICellRendererParams<TopicOverview>) => {
        const score = params.data?.visibility ?? 0;
        if (!score || score <= 0) return <span className="fs--2 text-300">—</span>;
        return <VisibilityBar score={score} />;
      },
    },
    {
      field: 'avgPosition',
      title: 'میانگین رتبه AI',
      tooltip: getHelpDescription('metrics.avg_ai_position'),
      type: 'number',
      width: 130,
      searchable: true,
      valueFormatter: (p: { value: number | null }) => (p.value != null ? `#${p.value}` : '—'),
    },
    {
      field: 'aiMentions',
      title: 'منشن در AI',
      tooltip: getHelpDescription('metrics.ai_mentions'),
      type: 'number',
      width: 110,
      searchable: true,
      valueFormatter: (p: { value: number | null }) => (p.value != null ? String(p.value) : '—'),
    },
    {
      field: 'topBrands',
      title: 'برندهای برتر',
      tooltip: getHelpDescription('tables.top_brands'),
      type: 'string',
      width: 120,
      minWidth: 100,
      maxWidth: 140,
      sortable: false,
      searchable: false,
      cellRenderer: (params: ICellRendererParams<TopicOverview>) => {
        const row = params.data;
        if (!row) return null;
        return (
          <PromptTopBrands
            status={row.executionCount > 0 ? 'executed' : 'pending'}
            runs={row.executionCount}
            brands={row.topBrands}
            max={3}
          />
        );
      },
    },
    {
      field: 'lastActivity',
      title: 'آخرین فعالیت',
      tooltip: getHelpDescription('tables.last_activity'),
      type: 'string',
      width: 130,
      searchable: true,
      valueFormatter: (p: { value: string | null }) => (p.value ? timeAgo(p.value) : '—'),
    },
    {
      field: '_actions',
      title: ' ',
      type: 'string',
      width: 44,
      minWidth: 44,
      maxWidth: 44,
      flex: 0,
      resizable: false,
      sortable: false,
      searchable: false,
      noFilter: true,
      pinned: 'left',
      lockPosition: true,
      cellClass: 'over-flow',
      cellStyle: { textAlign: 'center', margin: '0px' },
      cellRenderer: (params: ICellRendererParams<TopicOverview>) => {
        const row = params.data;
        if (!row) return null;
        const isExpanded = expandedTopicId === row.id;
        return (
          <div className="w-100 d-flex h-100 align-items-center justify-content-center">
            <span className="allaho_akbar_zIndex">
              <Dropdown
                onClick={e => e.stopPropagation()}
                align="start"
                className="allaho_akbar_zIndex overflow-visible btn-reveal-trigger d-inline-block me-0"
              >
                <Dropdown.Toggle
                  className="text-600 btn-reveal"
                  split
                  variant="reveal"
                  size="sm"
                >
                  <FontAwesomeIcon icon="ellipsis-h" className="fs--2" />
                </Dropdown.Toggle>
                <Dropdown.Menu
                  className="border py-0"
                  popperConfig={{ strategy: 'fixed' }}
                  renderOnMount
                >
                  <Dropdown.Item
                    onClick={() => onToggleTopic(row.id)}
                    className="link-600 fs--1 d-flex align-items-center gap-1 p-0 px-1"
                  >
                    <FontAwesomeIcon icon="eye" className="me-1 ms-2" />
                    {isExpanded ? 'بستن' : 'مشاهده'}
                  </Dropdown.Item>
                  <Dropdown.Divider className="my-0" />
                  <Dropdown.Item
                    onClick={() => onDeleteTopic(row)}
                    className="link-600 text-danger fs--1 d-flex align-items-center gap-1 p-0 px-1"
                  >
                    <FontAwesomeIcon icon="trash-alt" className="me-1 fs-0 ms-2" />
                    حذف
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </span>
          </div>
        );
      },
    },
  ];

  return cols;
}
